/**
 * Боковик: когда тренда нет.
 *
 * Слабый тренд (ADX ниже 20) стоит в обязательном фоне у торговых идей
 * раздела: границы диапазона держат только тогда, когда у рынка нет
 * силы их пробить.
 */
import { above, adx, alertOnly, atr, bb, below, close, crossDown, crossUp, happened, ind, level, mirror, number, premium, rsi, rvol, scaled, spec, weight, } from './kit.js';
export const RANGE_TEMPLATES = [
    {
        id: 'range-fade',
        name: 'Торговля в диапазоне',
        icon: '🌊',
        category: 'range',
        hint: 'Тренда нет: покупаем у нижней границы диапазона, продаём у верхней.',
        steps: ['Тренда нет', 'Цена дошла до границы диапазона', 'Свеча отказа'],
        keywords: ['боковик', 'диапазон', 'range', 'флэт'],
        passport: {
            level: 'novice',
            market: 'range',
            horizon: 'day',
            style: 'counter-trend',
            timeframe: '15m',
            idea: 'Пока у рынка нет силы, цена ходит между границами. Покупать у нижней, продавать у верхней.',
            why: 'В боковике у границ стоят встречные заявки, и цена от них отталкивается.',
            fails: 'Когда боковик заканчивается: граница пробивается, и последняя «покупка у поддержки» оказывается убыточной.',
            tip: 'Защиту ставьте сразу за границей: пробой диапазона — сигнал выходить, а не ждать.',
        },
        build: (o) => spec(o, 'Торговля в диапазоне', mirror((side) => ({
            context: [below(adx, number(20))],
            trigger: side.sign > 0 ? crossDown(premium(), number(20)) : crossUp(premium(), number(80)),
            confirmations: [weight('c1', happened(ind('pin_bar', { factor: 2 }, side.way)), 60), weight('c2', above(rvol, number(1.1)), 40)],
        })), 0),
    },
    {
        id: 'atr-compression',
        name: 'Сжатие волатильности',
        icon: '🤏',
        category: 'range',
        hint: 'Размах упал заметно ниже обычного — рынок затих. Направления здесь нет: это предупреждение.',
        steps: ['Размах бара упал ниже обычного', 'Сжатие глубокое', 'Объём и тренд затихли'],
        keywords: ['atr', 'волатильность', 'сжатие', 'squeeze'],
        passport: {
            level: 'novice',
            market: 'range',
            horizon: 'swing',
            style: 'alert',
            timeframe: '1H',
            idea: 'Свечи стали заметно мельче обычного. Рынок затих — а затишье обычно заканчивается сильным движением.',
            why: 'Низкая волатильность копит энергию: позиции накапливаются, стопы стоят плотно.',
            fails: 'Куда выйдет цена, идея не говорит — это предупреждение, а не совет.',
            tip: 'Включите оповещения: когда придёт сигнал, ставьте «Пробой экстремума» в обе стороны.',
        },
        build: (o) => spec(o, 'Сжатие волатильности', alertOnly({
            context: [],
            trigger: crossDown(atr(14), scaled(atr(50), 0.7)),
            confirmations: [
                weight('c1', below(atr(14), scaled(atr(50), 0.5)), 40),
                weight('c2', below(rvol, number(0.8)), 30),
                weight('c3', below(adx, number(20)), 30),
            ],
        }), 30),
    },
    {
        id: 'atr-expansion',
        name: 'Расширение волатильности',
        icon: '🌋',
        category: 'range',
        hint: 'Размах бара резко вырос — тишина кончилась. Тоже без стороны.',
        steps: ['Размах бара вырос выше обычного', 'Объём подтверждает'],
        keywords: ['atr', 'волатильность', 'расширение'],
        passport: {
            level: 'novice',
            market: 'any',
            horizon: 'day',
            style: 'alert',
            timeframe: '1H',
            idea: 'Свечи вдруг стали в полтора раза крупнее обычного — на рынке что-то происходит.',
            why: 'Расширение размаха — след новости или крупного участника; игнорировать его опасно.',
            fails: 'Сторону идея не называет: это сигнал «посмотри на график», а не вход.',
            tip: 'Хорошо дополняет ботов: при расширении стоит пересмотреть коридор сетки.',
        },
        build: (o) => spec(o, 'Расширение волатильности', alertOnly({
            context: [],
            trigger: crossUp(atr(14), scaled(atr(50), 1.5)),
            confirmations: [weight('c1', above(rvol, number(1.5)), 100)],
        }), 0),
    },
    /* ---------------------------------------------------------- новые */
    {
        id: 'bb-bounce-flat',
        name: 'Отскок от полосы в боковике',
        icon: '🏓',
        category: 'range',
        hint: 'Тренда нет, цена вернулась внутрь полосы Боллинджера — ход к противоположной стороне.',
        steps: ['Тренда нет (ADX ниже 20)', 'Цена вернулась внутрь полосы', 'RSI подтверждает крайность'],
        keywords: ['боллинджер', 'боковик', 'отскок', 'полоса'],
        passport: {
            level: 'novice',
            market: 'range',
            horizon: 'day',
            style: 'counter-trend',
            timeframe: '15m',
            idea: 'В боковике полосы Боллинджера работают как границы: цена выходит за полосу и возвращается к середине.',
            why: 'Без тренда выход за полосу — перегиб, а не начало движения.',
            fails: 'Если ADX начинает расти — боковик заканчивается, и полоса перестаёт держать.',
            tip: 'Цель — средняя линия полос; до противоположной полосы доходит реже.',
        },
        build: (o) => spec(o, 'Отскок от полосы в боковике', mirror((side) => ({
            context: [below(adx, number(20))],
            trigger: side.cross(close, bb(side.sign > 0 ? 'lower' : 'upper')),
            confirmations: [weight('c1', side.sign > 0 ? below(rsi, number(40)) : above(rsi, number(60)), 60), weight('c2', above(rvol, number(1.1)), 40)],
        })), 0),
    },
    {
        id: 'rsi-corridor',
        name: 'RSI в коридоре 40–60',
        icon: '↔️',
        category: 'range',
        hint: 'В спокойном рынке RSI дошёл до края своего коридора и повернул обратно.',
        steps: ['Тренда нет', 'RSI вернулся из-за 40 или 60', 'Цена у края диапазона'],
        keywords: ['rsi', 'коридор', 'боковик', '40', '60'],
        passport: {
            level: 'skilled',
            market: 'range',
            horizon: 'day',
            style: 'counter-trend',
            timeframe: '15m',
            idea: 'В боковике RSI редко доходит до 30 и 70 — он колеблется между 40 и 60. Эти границы и работают.',
            why: 'Спокойный рынок не даёт крайностей, и мерить его надо по его собственному размаху.',
            fails: 'При начале тренда RSI выходит из коридора и не возвращается.',
            tip: 'Сочетайте с «Торговлей в диапазоне»: два признака края надёжнее одного.',
        },
        build: (o) => spec(o, 'RSI в коридоре 40–60', mirror((side) => ({
            context: [below(adx, number(18))],
            trigger: side.cross(rsi, number(level(side, 40))),
            confirmations: [weight('c1', side.sign > 0 ? below(premium(), number(40)) : above(premium(), number(60)), 60), weight('c2', above(rvol, number(1.1)), 40)],
        })), 0),
    },
];
//# sourceMappingURL=range.js.map